/**
 * ProjectCard — Option B (full-bleed hero + scrim overlay).
 *
 * Designed to be visually consistent with CommunitiesPage community cards.
 * Displays:
 *   - Hero image (or gradient placeholder when none)
 *   - Role badge (top-left) with optional lock icon for private projects
 *   - Country flag badge (top-right) via entity lookup
 *   - Bottom scrim: project name + "Category · Community"
 *   - Hover overlay: description + status dot + reach level
 */

import React from 'react';
import styles from './ProjectCard.module.css';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { getKpiIndicator } from '../../utils/helpers';
import { FIELD_TYPES } from '../../lib/constants';
import { mediaSrc } from '../../api/mediaUrl';
import CardHeroMenu from './CardHeroMenu';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ProjectCardData {
  /** Normalised project GUID (uppercase). */
  project_guid: string;
  project_name: string;
  /** Full URL to hero/tile image, or empty string / null if none. */
  card_image_url?: string | null;
  hero_image_url?: string | null;
  tile_image_url?: string | null;
  project_category?: string;
  project_status?: string;
  project_reach?: string;
  description?: string;
  /** Display-level community string, e.g. "Hampton, NH". */
  Community?: string;
  community_l3?: string;
  community_l2?: string;
  community_l1?: string;
  /** Link to community entity for flag resolution. */
  primary_location_entity_guid?: string;
  /** Visibility (for lock icon). */
  visibility?: string;
}

/** A segment in the community path (leaf + ancestors). */
export interface CommunityPathSegment {
  guid: string;
  name: string;
  isActive: boolean;
}

export interface ProjectCardProps {
  project: ProjectCardData;
  /** User's role on this project (e.g. "owner_admin", "admin", "viewer"). */
  role?: string;
  /** Resolved country flag emoji (pre-computed by parent). */
  countryFlag?: string;
  /** Resolved country name (pre-computed by parent). */
  countryName?: string;
  /** Whether this card is currently selected (highlighted border). */
  selected?: boolean;
  onClick?: (projectGuid: string) => void;
  /** Full community path segments (leaf first, then ancestors). Active segments become links. */
  communityPath?: CommunityPathSegment[];
  /** Navigate to a community page by guid. */
  onCommunityClick?: (communityGuid: string) => void;
  /**
   * DEV-657: Enriched Data row for rendering KPI shape indicators along the
   * bottom of the card. When provided, 5 small KPI shapes (Summary, Schedule,
   * Finance, Mission, Data) are rendered. Each shape is clickable.
   */
  kpiRow?: any;
  /** DEV-657: Called when a KPI shape is clicked. Index: 0–4 (Summary → Data). */
  onKpiClick?: (projectGuid: string, kpiTabIndex: number) => void;
  /** When true, shows a three-dot menu for changing the card hero image. */
  canEditHero?: boolean;
  /** Callback fired after the hero image is changed via the card menu. */
  onHeroChanged?: () => void;
  /**
   * DEV-1260. Optional hero persistence override for the card menu.
   * Full-project cards persist via the projectConfigOverrides store
   * (default); Mini-Project cards pass a function that PATCHes the
   * entity instead. Receives the new image URL ('' = remove).
   */
  persistHero?: (url: string) => void;
  /**
   * Skip 2026-05-28. MinIO bucket for card-image uploads. Defaults to
   * 'heroes' (FP). Mini-Project cards must pass 'mini-project-hero' or
   * uploads fail at the storage layer.
   */
  uploadBucket?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Pretty-print role labels. */
function roleLabel(role: string): string {
  switch (role) {
    case 'owner_admin': return 'Owner';
    case 'admin': return 'Admin';
    case 'viewer': return 'Viewer';
    case 'content_contributor': return 'Contributor';
    case 'content_creator': return 'Creator';
    case 'sponsor': return 'Sponsor';
    case 'lead': return 'Lead'; // DEV-1260: mini-project Lead
    default: return role;
  }
}

/* ------------------------------------------------------------------ */
/*  Inline SVG icons                                                   */
/* ------------------------------------------------------------------ */

function LockIcon() {
  return (
    <svg className={styles.lockIcon} viewBox="0 0 16 16" fill="none">
      <path
        d="M12 7H4a1 1 0 00-1 1v5a1 1 0 001 1h8a1 1 0 001-1V8a1 1 0 00-1-1z"
        fill="currentColor"
        opacity={0.9}
      />
      <path
        d="M5.5 7V5a2.5 2.5 0 015 0v2"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Compass icon — identifies this card as a Project. */
function ProjectTypeIcon() {
  return (
    <span className={styles.typeBadge} data-type="project">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    </span>
  );
}


/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/** KPI tab fields in display order: Summary, Schedule, Finance, Mission, Data. */
const KPI_FIELDS = [
  FIELD_TYPES.SUMMARY,
  FIELD_TYPES.SCHEDULE,
  FIELD_TYPES.FINANCE,
  FIELD_TYPES.MISSION,
  FIELD_TYPES.DATA,
];

/** Abbreviated labels shown beneath each KPI shape in the frosted pill bar. */
const KPI_ABBR = ['Sum', 'Sch', 'Bud', 'Mis', 'Dat'];

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  role,
  countryFlag,
  countryName,
  selected,
  onClick,
  communityPath,
  onCommunityClick,
  kpiRow,
  onKpiClick,
  canEditHero,
  onHeroChanged,
  persistHero,
  uploadBucket,
}) => {
  const { t } = useAppTranslation('pages');
  // Card image priority: card-specific thumbnail → main hero → fallback tile → none
  const heroUrl = mediaSrc(project.card_image_url || project.hero_image_url || project.tile_image_url || null);

  /* DEV-1463 — the lock icon is REMOVED here, deliberately.
   *
   * It rendered on `visibility === 'private'`. Two problems, both fatal:
   *
   *   1. NO TIER HAS EVER HAD A 'private' VALUE. Mini-projects use
   *      team_only | community | public (migration 165); full projects had no
   *      visibility column at all until migration 275, which adopts the same
   *      three values. So this predicate has been false for every project, of
   *      every tier, since it shipped. It is not "a lock that broke" — it is a
   *      lock that never once fired.
   *
   *   2. It advertised a protection the platform does not provide.
   *      `GET /api/projects` is STILL ungated (DEV-1071). Wiring the icon to the
   *      new real column now would be worse than leaving it dead: a lock that
   *      displays but does not lock is the enabled-then-fail deception pattern
   *      CLAUDE.md forbids — the UI would be asserting a guarantee the server
   *      does not honour.
   *
   * DEV-1071 (the GATE slice) restores it truthfully, once there is something
   * true to say.
   */
  const community =
    project.Community || project.community_l3 || project.community_l2 || project.community_l1 || '';
  const category = project.project_category || '';
  const status = (project.project_status || '').toLowerCase();
  const statusDisplay = project.project_status || '';
  const reach = project.project_reach || '';
  const description = project.description || '';

  return (
    <div
      role="button"
      tabIndex={0}
      className={styles.card}
      data-selected={selected ? 'true' : undefined}
      data-has-kpi={kpiRow ? 'true' : undefined}
      onClick={() => onClick?.(project.project_guid)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(project.project_guid); } }}
      title={project.project_name}
    >
      {/* Accent strip — medium grey left highlight */}
      <div className={styles.accentStrip} />

      {/* Hero image or placeholder */}
      {heroUrl ? (
        <div className={styles.heroWrap}>
          <img src={heroUrl} alt="" className={styles.heroImg} loading="lazy" />
        </div>
      ) : (
        <div className={styles.placeholder}>
          <span className={styles.placeholderIcon}>{'\u{1F30D}'}</span>
        </div>
      )}

      {/* B4 identity block — country + flag above, project name below */}
      {kpiRow && (
        <div className={styles.b4Identity}>
          {countryFlag && countryName && (
            <span className={styles.countryPillB4}>
              <span className={styles.countryPillB4Flag}>{countryFlag}</span>
              {countryName}
            </span>
          )}
          <div className={styles.namePill}>{project.project_name}</div>
        </div>
      )}

      {/* Badge bar — type icon + role (left) · country (right) — hidden in B4 mode via CSS */}
      <div className={styles.badgeBar}>
        <div className={styles.badgeBarLeft}>
          <ProjectTypeIcon />
          {role && (
            <span className={styles.roleBadge} data-role={role}>
              {/* DEV-1463 — lock icon removed. It never fired (no tier has a
                  'private' value) and it advertised a gate that does not exist.
                  DEV-1071 restores it truthfully. See the note above. */}
              {roleLabel(role)}
            </span>
          )}
        </div>
        {countryFlag && countryName && (
          <span className={styles.countryBadge}>
            <span className={styles.countryFlag}>{countryFlag}</span>
            {countryName}
          </span>
        )}
      </div>

      {/* Hero image menu (three-dot) — only when edit is permitted */}
      {canEditHero && (
        <CardHeroMenu
          projectGuid={project.project_guid}
          currentHeroUrl={heroUrl}
          onHeroChanged={onHeroChanged}
          persistOverride={persistHero}
          uploadBucket={uploadBucket}
        />
      )}

      {/* Bottom scrim (always visible, fades on hover) */}
      <div className={styles.scrim}>
        <div className={styles.scrimName}>{project.project_name}</div>
        {/* Hide meta line when KPI pill is present — country badge already shows location */}
        {!kpiRow && (category || community || (communityPath && communityPath.length > 0)) && (
          <div className={styles.scrimMeta}>
            {category && <span>{category}</span>}
            {category && (community || (communityPath && communityPath.length > 0)) && (
              <span className={styles.scrimDot}>{'\u00B7'}</span>
            )}
            {communityPath && communityPath.length > 0 ? (
              <span className={styles.communityPath}>
                {communityPath.map((seg, i) => (
                  <React.Fragment key={seg.guid}>
                    {i > 0 && <span className={styles.pathSep}>{',\u00A0'}</span>}
                    {seg.isActive && onCommunityClick ? (
                      <span
                        role="link"
                        tabIndex={0}
                        className={styles.communityLink}
                        onClick={(e) => {
                          e.stopPropagation();
                          onCommunityClick(seg.guid);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.stopPropagation(); onCommunityClick(seg.guid); }
                        }}
                      >
                        {seg.name}
                      </span>
                    ) : (
                      <span>{seg.name}</span>
                    )}
                  </React.Fragment>
                ))}
              </span>
            ) : (
              community && <span>{community}</span>
            )}
          </div>
        )}
      </div>

      {/* Hover overlay — description + status */}
      <div className={styles.overlay}>
        <div className={styles.overlayName}>{project.project_name}</div>
        {description && <p className={styles.overlayDesc}>{description}</p>}
        {(statusDisplay || reach) && (
          <div className={styles.overlayMeta}>
            {statusDisplay && (
              <>
                <span className={styles.statusDot} data-status={status} />
                <span>{statusDisplay}</span>
              </>
            )}
            {statusDisplay && reach && <span>{'\u00B7'}</span>}
            {reach && <span>{reach}</span>}
          </div>
        )}
      </div>

      {/* DEV-657: KPI shape indicators — frosted pill bar (A3 design) */}
      {kpiRow && (
        <div
          className={styles.kpiPill}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Enter') e.stopPropagation(); }}
        >
          {KPI_FIELDS.map((field, i) => (
            <div
              key={field}
              role="button"
              tabIndex={0}
              className={styles.kpiPillShape}
              onClick={(e) => {
                e.stopPropagation();
                onKpiClick?.(project.project_guid, i);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  onKpiClick?.(project.project_guid, i);
                }
              }}
              title={field.charAt(0).toUpperCase() + field.slice(1)}
              aria-label={field}
            >
              {getKpiIndicator(field, kpiRow, 'sm')}
              <span className={styles.kpiPillLabel}>{KPI_ABBR[i]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectCard;
